/**
 * PersonAvatar Component
 *
 * Displays a person's profile image or a gender-based fallback avatar.
 * Handles missing images gracefully with visually polished defaults.
 */

interface PersonAvatarProps {
  imageUrl?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  firstName: string;
  lastName?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'size-12',
  md: 'size-16',
  lg: 'size-20',
  xl: 'size-32',
};

const textSizeClasses = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl',
};

/**
 * Get initials from name
 */
function getInitials(firstName: string, lastName?: string): string {
  const first = firstName.charAt(0).toUpperCase();
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return `${first}${last}`;
}

/**
 * Get avatar background and border colors based on gender
 */
function getGenderColors(gender?: 'male' | 'female' | 'other' | null) {
  switch (gender) {
    case 'male':
      return {
        background: 'bg-primary/10',
        border: 'border-primary/20',
        text: 'text-primary',
      };
    case 'female':
      return {
        background: 'bg-pink-50 dark:bg-pink-900/20',
        border: 'border-pink-200 dark:border-pink-700/30',
        text: 'text-pink-600 dark:text-pink-400',
      };
    default:
      return {
        background: 'bg-slate-100 dark:bg-slate-800',
        border: 'border-slate-200 dark:border-slate-700',
        text: 'text-slate-600 dark:text-slate-400',
      };
  }
}

export function PersonAvatar({
  imageUrl,
  gender,
  firstName,
  lastName,
  size = 'md',
  className = '',
}: PersonAvatarProps) {
  const colors = getGenderColors(gender);
  const initials = getInitials(firstName, lastName);

  if (imageUrl) {
    return (
      <div
        className={`${sizeClasses[size]} rounded-full overflow-hidden border-2 ${colors.border} ${className}`}
      >
        <img
          src={imageUrl}
          alt={`${firstName} ${lastName || ''}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            // If image fails to load, hide it and show fallback
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>
    );
  }

  // Fallback: Show gender-based icon
  const genderIcon = gender === 'male' ? 'man' : gender === 'female' ? 'woman' : 'person';

  return (
    <div
      className={`${sizeClasses[size]} rounded-full ${colors.background} border-2 ${colors.border} flex items-center justify-center ${className}`}
    >
      <span className={`material-symbols-outlined ${textSizeClasses[size]} ${colors.text}`}>
        {genderIcon}
      </span>
    </div>
  );
}
